import { z } from "zod";

import { enumReadSchema } from "../../../../shared/core/enum-read.schema";
import {
  VibeBuildJobSchema,
  VibeDeploymentSchema,
  VibeTriggerShaSchema
} from "./deployments.schemas";
import { VibeRefNameSchema, VibeRefSchema } from "./refs.schemas";

/**
 * What became of a commit, as ONE value a caller switches on.
 *
 * This enum is the whole point of the endpoint. A client that has just
 * pushed can today only infer the answer from `git`'s own stdout, and that
 * text cannot be classified reliably: rejection lines print FIRST (so a pipe
 * through `tail` destroys them), `-q` suppresses the success report but not
 * the failure report, a backgrounded push carries no outcome at all, and an
 * error message quoting a remote URL is byte-identical in shape to a success
 * report. The measured consequence is failures read as successes — the
 * dangerous direction, because it arms a verification obligation for a deploy
 * that never happened.
 *
 * So the platform answers instead, from the rows it already writes, and it
 * answers with a discriminator rather than with fields the caller must
 * correlate. `deployment === null` alone is ambiguous — it means "your push
 * never arrived" OR "it arrived and nothing was configured to deploy it" —
 * and re-deriving that distinction at every client is how a second, drifting
 * definition gets born.
 */
export const VIBE_DEPLOY_STATE_OUTCOME_VALUES = [
  /**
   * A deployment exists for this commit. `deployment.status` says what
   * became of it; this value says only that the platform got as far as
   * creating one.
   */
  "DEPLOYED",
  /**
   * The ref carries this commit as its head, and no deployment was created
   * from it. The push LANDED — this is not a failure. The ordinary causes are
   * the ones the receive hook already names back on the push channel: the ref
   * is not the app's deploy branch, no app is attached to the repository, or
   * every deploy was refused (a suspended organisation being the usual cause).
   */
  "RECEIVED_NOT_DEPLOYED",
  /**
   * No ref on this app's repository has this commit as its head, and no
   * deployment exists for it.
   *
   * ⚠️ Read this as "the platform cannot see it", not as "it was rejected".
   * Ref rows record HEADS, not history, so a commit that landed and was then
   * pushed past reads NOT_RECEIVED here even though it did arrive. That is
   * the safe direction — it under-claims success, where the text parser this
   * replaces over-claimed it — but it is still a reason to ask about the sha
   * you just pushed rather than one from an hour ago. `resolved.refName`'s
   * current head is the honest thing to compare against.
   */
  "NOT_RECEIVED",
  /**
   * The named ref does not exist on this app's repository at all — nothing
   * has ever been pushed to it. Distinct from NOT_RECEIVED, which is about a
   * commit; this is about the ref itself.
   */
  "REF_UNKNOWN",
  /**
   * The app has no git project attached, so there is no repository to have
   * received anything. The same fact the app's `deployability` publishes as
   * `NO_SOURCE_ATTACHED`.
   */
  "NO_REPOSITORY"
] as const;

export const VibeDeployStateOutcomeSchema = z.enum(VIBE_DEPLOY_STATE_OUTCOME_VALUES);
export type VibeDeployStateOutcomeValue = z.infer<typeof VibeDeployStateOutcomeSchema>;

/**
 * The outcome as a reader parses it: a value this contract does not list reads
 * as unlisted instead of failing the response.
 *
 * Lenient, unlike the deployment's status, because an unlisted outcome has an
 * honest rendering — "an outcome this version does not know" claims nothing
 * about the push — and every other field still answers on its own: `deployment`,
 * `live` and `served` say what happened whether or not the summary word is
 * known. Failing the parse would take all of them down to protect a word.
 */
export const VibeDeployStateOutcomeReadSchema = enumReadSchema(VIBE_DEPLOY_STATE_OUTCOME_VALUES);
export type VibeDeployStateOutcomeReadValue = z.infer<typeof VibeDeployStateOutcomeReadSchema>;

/**
 * How the commit under question was arrived at. Surfaced because the endpoint
 * accepts three ways of asking and a caller that omitted both parameters is
 * otherwise unable to tell WHICH commit it was answered about.
 */
export const VIBE_DEPLOY_STATE_RESOLVED_FROM_VALUES = ["sha", "ref", "deployBranch"] as const;
export const VibeDeployStateResolvedFromSchema = z.enum(VIBE_DEPLOY_STATE_RESOLVED_FROM_VALUES);
export type VibeDeployStateResolvedFromValue = z.infer<typeof VibeDeployStateResolvedFromSchema>;

/**
 * Provenance only, so an unlisted way of resolving the commit renders as its own
 * word and changes nothing else a reader does.
 */
export const VibeDeployStateResolvedFromReadSchema = enumReadSchema(
  VIBE_DEPLOY_STATE_RESOLVED_FROM_VALUES
);
export type VibeDeployStateResolvedFromReadValue = z.infer<
  typeof VibeDeployStateResolvedFromReadSchema
>;

export const GetVibeDeployStatePathVarsSchema = z.object({
  appId: z.string().uuid()
});

export const GetVibeDeployStateQuerySchema = z
  .object({
    /**
     * Fully-qualified ref (`refs/heads/main`). Its current head is the commit
     * the answer is about. Omit both parameters to ask about the app's own
     * deploy branch, which is what a caller who just ran `git push` wants and
     * cannot always name — an app whose code lives on `main` may deploy from
     * `release/prod`.
     */
    ref: VibeRefNameSchema.optional(),
    /**
     * Ask about one exact commit. Accepts the short and full hex forms, for
     * the same reason the deploy trigger does — `git rev-parse --short HEAD`
     * is what a script has to hand.
     *
     * Prefer this over `ref` immediately after a push: it is the only form
     * that stays correct once the ref advances past your commit.
     */
    sha: VibeTriggerShaSchema.optional()
  })
  .strict()
  .refine((q) => q.ref === undefined || q.sha === undefined, {
    message:
      "pass ref or sha, never both — they are two different questions and there is no single answer to both"
  });

/**
 * The deployment occupying the live slot, and where it answers.
 *
 * ⚠️ READ FROM THE CONTROL PLANE, deliberately, and not as a convenience:
 * the `X-Vibe-Deployment` / `X-Vibe-Commit` response headers cannot answer
 * this in the common case. They are stamped by a Traefik middleware chained
 * AFTER the edge authorizer, so a PRIVATE app returns a 401 carrying no stamp
 * at all — and PRIVATE is the DEFAULT visibility. A header-only identity check
 * verifies public apps and silently declines to verify everything else.
 *
 * `null` when nothing is live: never deployed, or every version is down. It is
 * an independent signal from `deployment` above and the two routinely
 * disagree — a failed newest build over a still-live older version is the
 * normal state after a bad push, and collapsing them into one badge is the
 * exact mistake this shape exists to prevent.
 *
 * 🔴 Named `live`, not `served`, and `servedProvenAt` below is why. This
 * object is the newest HEALTHY deployment, which is the ALLOCATION's verdict —
 * it precedes the edge swap by up to whole minutes. A client that verifies a
 * deploy on healthiness alone reads the OLD content and concludes its new code
 * is live.
 */
export const VibeLiveDeploymentSchema = z.object({
  deploymentId: z.string().uuid(),
  /// User-facing monotonic version (`v{n}`) of what is answering right now.
  versionNumber: z.number().int(),
  /// The commit that built the image currently serving.
  commitSha: z.string(),
  /**
   * The app's canonical public URL, or `null` for an app created before the
   * URL was stamped. Null is "we cannot name it", never "it is not live" —
   * the presence of this object is what says something is live.
   */
  url: z.string().nullable(),
  /**
   * When the platform last OBSERVED the edge answering with THIS deployment —
   * the `DEPLOYMENT_SERVED` audit row, written by the served probe only after
   * a real request over the public internet came back stamped with this
   * deployment id.
   *
   * This is the field to check before believing a deploy is verifiable.
   *
   * 🔴 `null` means NOT PROVEN. It NEVER means "not live", and a reader who
   * treats it as "not serving" reinvents the bug this field exists to close.
   * The probe runs on a sweep, so a deployment that went healthy moments ago
   * is legitimately unproven; an app with no `publicUrl` has nothing to probe
   * and stays `null` forever while serving perfectly well. The object's own
   * PRESENCE is what says something is live — this field only ever adds proof,
   * never withdraws it.
   *
   * **When it is `null`, read `served` at the top level to find out why.** That
   * object names the deployment the edge WAS last observed answering with, so
   * the two readings of this null — "proof has not arrived yet" and "the edge
   * is demonstrably still on the previous build" — become distinguishable
   * instead of collapsing into one absence.
   *
   * The gap it closes is measured, not theoretical: healthy precedes served by
   * up to whole minutes, and a post-deploy check that fires in that window
   * reads the previous version and passes.
   */
  servedProvenAt: z.coerce.date().nullable(),
  /**
   * When this deployment was TRIGGERED — not when it went healthy, and the
   * difference is deliberate rather than an oversight.
   *
   * No column records the healthy transition: `updatedAt` moves again on
   * every later write to the row, including the screenshot capture that runs
   * minutes afterwards, so reading it as "serving since" would report a time
   * that drifts forward while nothing about the deployment changed. The
   * trigger instant is a fact the row genuinely holds. The audit feed
   * (`DEPLOYMENT_HEALTHY`) is where the healthy instant lives if a caller
   * needs it exactly.
   */
  createdAt: z.coerce.date()
});

export type VibeLiveDeploymentDto = z.infer<typeof VibeLiveDeploymentSchema>;

/**
 * The deployment the platform LAST OBSERVED the edge answering with — the
 * served-artifact identity, read from the control plane rather than from a
 * response header.
 *
 * 🔴 THIS IS AN OBSERVATION WITH A TIMESTAMP, NOT A LIVE READING, AND THE
 * DIFFERENCE IS THE WHOLE REASON `provenAt` IS MANDATORY HERE. The row is
 * written by the served probe when a real request over the public internet came
 * back stamped with this deployment id, and nothing re-checks it afterwards. A
 * rollback, a teardown or a fresh deploy since `provenAt` is not reflected. A
 * reader that renders this as "currently served" without showing how old it is
 * repeats — one layer up — the exact mistake of reading `DEPLOYMENT_HEALTHY` as
 * "served".
 *
 * ⚠️ It names a deployment, never a status. The deployment it names may since
 * have been superseded, rolled back or soft-deleted, which is precisely why the
 * fields below are copied from the audit payload instead of being re-read from
 * the deployment row: the observation is immutable and the row is not, so
 * enriching it would let a later status contradict the fact being reported.
 * `versionNumber` is absent for the same reason — it lives only on the row.
 *
 * **How it relates to `live` above, exhaustively — there is no fourth case:**
 *
 * - `served === null` — the edge has never been observed answering with any
 *   deployment of this app. Never deployed, no `publicUrl` to probe, or every
 *   probe so far came back on the previous build.
 * - `served.deploymentId === live.deploymentId` — the swap is done and proven.
 *   `live.servedProvenAt` equals `served.provenAt` in this case, and this is the
 *   ONLY case in which `live.servedProvenAt` is non-null.
 * - `served.deploymentId !== live.deploymentId` — as of `provenAt` the edge was
 *   still answering with the PREVIOUS build. `live.servedProvenAt` is `null`,
 *   and this object is what that null means.
 *
 * That third case is the one this schema exists for. Before it, a client that
 * had just pushed saw only `servedProvenAt: null` and could not tell "proof is
 * still coming" from "the edge is demonstrably still on the old build" — so it
 * fell back to polling the app for a content marker, which is the workaround
 * this endpoint is meant to retire.
 */
export const VibeServedArtifactSchema = z.object({
  /// The deployment the edge answered with when it was last observed.
  deploymentId: z.string().uuid(),
  /**
   * The commit whose build answered. This is the field that settles "is what I
   * pushed what is live" — compare it against the sha you pushed, in one call,
   * with no content marker and no HTML to grep.
   */
  commitSha: z.string(),
  /// The exact image the edge answered from. Two deployments can carry the same
  /// commit (a `forceRebuild`, a redeploy); the image ref never collides.
  imageRef: z.string(),
  /**
   * When the observation was made — NOT when the deployment went live, which
   * the platform does not witness. Always compare this against the present
   * before relaying the answer; an observation from an hour ago says nothing
   * about the last hour.
   */
  provenAt: z.coerce.date(),
  /**
   * Milliseconds from that deployment being marked HEALTHY to this observation:
   * the healthy-to-served lag, measured rather than assumed.
   *
   * An UPPER bound on the real lag, never the lag itself — the probe samples on
   * a sweep, so it can only notice the swap some time after it happened. Useful
   * for "is this getting worse?", wrong for an SLO.
   */
  healthyToServedMs: z.number().int().nonnegative()
});

export type VibeServedArtifactDto = z.infer<typeof VibeServedArtifactSchema>;

export const GetVibeDeployStateResponseSchema = z.object({
  /**
   * The single value to branch on. Every other field is detail supporting it.
   */
  outcome: VibeDeployStateOutcomeReadSchema,
  resolved: z.object({
    /**
     * The commit the answer is about, exactly as it was matched. `null` when
     * no commit could be resolved — an unknown ref, or an app with no
     * repository.
     */
    sha: z.string().nullable(),
    /**
     * The ref the answer is attached to. When the caller named a `ref` (or
     * named nothing, and the app's deploy branch was used) this is that ref.
     * When the caller named a bare `sha` it is the ref whose head IS that
     * commit, or `null` when no ref head matches — which is what separates
     * `RECEIVED_NOT_DEPLOYED` from `NOT_RECEIVED`.
     */
    refName: z.string().nullable(),
    from: VibeDeployStateResolvedFromReadSchema
  }),
  /**
   * The platform's own record of the ref — its head and when that head last
   * moved. This is the receipt: a row here is the control plane saying it
   * received a push on this ref, which is the question `git` stdout cannot
   * answer. `null` when the ref does not exist or was never consulted.
   */
  ref: VibeRefSchema.nullable(),
  /**
   * The newest deployment for the resolved commit, `null` when none exists.
   * Read `outcome` first: `null` here means different things depending on it.
   */
  deployment: VibeDeploymentSchema.nullable(),
  /**
   * The sibling build job of `deployment`, inlined so one call also answers
   * "why did it fail" — the build's `errorReason` and `logsRef` are where a
   * failed deploy explains itself. `null` when there is no deployment, or on
   * the rare deployment with no build-job row.
   */
  buildJob: VibeBuildJobSchema.nullable(),
  live: VibeLiveDeploymentSchema.nullable(),
  /**
   * The served-artifact identity: what the edge was last observed ANSWERING
   * with, as opposed to `live`, which is what the allocation reports HEALTHY.
   *
   * 🔴 The two are independent and routinely disagree for whole minutes after a
   * deploy. Collapsing them into one badge is the defect this pair exists to
   * prevent — read `VibeServedArtifactSchema` before doing anything with either.
   */
  served: VibeServedArtifactSchema.nullable()
});

export type GetVibeDeployStateResponseDto = z.infer<typeof GetVibeDeployStateResponseSchema>;
